(() => {
  'use strict';

  const storySelector = '[data-craftsmanship-story]';
  const stageCount = 4;
  const WHEEL_THRESHOLD = 40;
  const TRANSITION_DURATION = 650;
  const TRANSITION_MIDPOINT = TRANSITION_DURATION / 2;
  const TRANSITION_LOCK = 700;
  const MAGNIFIER_SIZE = 160;
  const MAGNIFIER_ZOOM = 2.5;
  const MAGNIFIER_READY_DELAY = 120;
  const revealFocals = [
    { x: '50%', y: '48%' },
    { x: '78%', y: '28%' },
    { x: '78%', y: '72%' },
  ];
  const registry = window.__skycorpsCraftsmanshipStoryRegistry || {
    controllers: new Map(),
    bound: false,
  };

  window.__skycorpsCraftsmanshipStoryRegistry = registry;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function getRootFromEventTarget(target) {
    if (!target || typeof target.matches !== 'function') return null;
    if (target.matches(storySelector)) return target;
    if (typeof target.querySelector !== 'function') return null;
    return target.querySelector(storySelector);
  }

  function createController(root) {
    if (!root || registry.controllers.has(root)) return null;

    const panel = root.querySelector('.story-panel');
    const visualStage = root.querySelector('.visual-stage');
    const imageLayers = [...root.querySelectorAll('[data-image-layer]')];
    const detailFocuses = [...root.querySelectorAll('[data-detail-focus]')];
    const copyPanels = [...root.querySelectorAll('[data-copy-panel]')];
    const stageLinks = [...root.querySelectorAll('[data-stage-link]')];
    const currentStageLabel = root.querySelector('[data-current-stage]');
    const grazingLight = root.querySelector('.story-grazing-light');
    const mobileQuery = window.matchMedia('(max-width: 700px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentStage = 0;
    let lastRenderedStage = -1;
    let wheelAccumulator = 0;
    let wheelDirection = 0;
    let isTransitioning = false;
    let transitionTimer = 0;
    let stageCommitTimer = 0;
    let unlockTimer = 0;
    let transitionToken = 0;
    let magnifierStage = currentStage;
    let magnifierReady = false;
    let isPointerInsideVisual = false;
    let isPointerOnImage = false;
    let lastPointer = null;
    let magnifierRevealTimer = 0;

    if (!panel || !visualStage || imageLayers.length !== stageCount) return null;

    function renderCurrentStage() {
      if (currentStage === lastRenderedStage) return;

      lastRenderedStage = currentStage;
      if (currentStageLabel) {
        currentStageLabel.textContent = String(currentStage + 1).padStart(2, '0');
      }

      copyPanels.forEach((copyPanel, index) => {
        const isActive = index === currentStage;
        copyPanel.classList.toggle('is-active', isActive);
        copyPanel.setAttribute('aria-hidden', String(!isActive));
      });

      stageLinks.forEach((link, index) => {
        if (index === currentStage) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    function getDetailFocus(stage) {
      return detailFocuses.find((detail) => Number(detail.dataset.detailFocus) === stage) || null;
    }

    function getStageImage(stage) {
      return imageLayers[stage] ? imageLayers[stage].querySelector('img') : null;
    }

    function isMagnifierStage(stage) {
      return stage === 1 || stage === 2;
    }

    function getPositionOffset(token, available, axis) {
      const value = String(token || '50%').trim().toLowerCase();
      const keywords = {
        left: axis === 'x' ? 0 : 0.5,
        right: axis === 'x' ? 1 : 0.5,
        top: axis === 'y' ? 0 : 0.5,
        bottom: axis === 'y' ? 1 : 0.5,
        center: 0.5,
      };

      if (Object.prototype.hasOwnProperty.call(keywords, value)) {
        return available * keywords[value];
      }

      if (value.endsWith('%')) {
        const percentage = Number.parseFloat(value);
        return available * clamp(Number.isFinite(percentage) ? percentage / 100 : 0.5, 0, 1);
      }

      if (value.endsWith('px')) {
        const pixels = Number.parseFloat(value);
        return clamp(Number.isFinite(pixels) ? pixels : available / 2, 0, available);
      }

      return available / 2;
    }

    function getContainBounds(image) {
      if (!image || !image.naturalWidth || !image.naturalHeight) return null;

      const container = image.closest('.story-image') || image;
      const rect = container.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;

      const scale = Math.min(rect.width / image.naturalWidth, rect.height / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const availableX = rect.width - width;
      const availableY = rect.height - height;
      const positions = getComputedStyle(image).objectPosition.trim().split(/\s+/);
      let xToken = positions[0] || '50%';
      let yToken = positions[1] || '50%';

      if (positions.length === 1 && (xToken === 'top' || xToken === 'bottom')) {
        yToken = xToken;
        xToken = '50%';
      }

      return {
        left: rect.left + getPositionOffset(xToken, availableX, 'x'),
        top: rect.top + getPositionOffset(yToken, availableY, 'y'),
        width,
        height,
      };
    }

    function setMagnifierSource(stage) {
      const detail = getDetailFocus(stage);
      const image = getStageImage(stage);
      const lens = detail ? detail.querySelector('.detail-lens') : null;
      const source = image ? image.currentSrc || image.src : '';

      if (!lens || !source) return;

      const escapedSource = source.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      lens.style.backgroundImage = `url("${escapedSource}")`;
    }

    function renderDetails() {
      const magnifierAllowed =
        !mobileQuery.matches &&
        !reducedMotionQuery.matches &&
        isMagnifierStage(magnifierStage) &&
        Boolean(getDetailFocus(magnifierStage));

      visualStage.classList.toggle('is-magnifier-enabled', magnifierAllowed);

      detailFocuses.forEach((detail) => {
        const detailStage = Number(detail.dataset.detailFocus);
        const shouldShow = magnifierAllowed && magnifierReady && isPointerOnImage && detailStage === magnifierStage;

        detail.style.opacity = shouldShow ? '1' : '0';
        detail.setAttribute('aria-hidden', String(!shouldShow));
        if (!shouldShow) detail.style.transform = 'translate3d(-80px, -80px, 0)';
      });
    }

    function updateMagnifier(pointer) {
      lastPointer = pointer ? { clientX: pointer.clientX, clientY: pointer.clientY } : null;

      const allowed =
        !mobileQuery.matches &&
        !reducedMotionQuery.matches &&
        magnifierReady &&
        isPointerInsideVisual &&
        isMagnifierStage(magnifierStage);
      const detail = getDetailFocus(magnifierStage);
      const image = getStageImage(magnifierStage);
      const lens = detail ? detail.querySelector('.detail-lens') : null;
      const bounds = allowed ? getContainBounds(image) : null;

      if (!allowed || !detail || !lens || !bounds || !pointer) {
        isPointerOnImage = false;
        renderDetails();
        return;
      }

      const pointerX = pointer.clientX;
      const pointerY = pointer.clientY;
      const isOnImage =
        pointerX >= bounds.left &&
        pointerX <= bounds.left + bounds.width &&
        pointerY >= bounds.top &&
        pointerY <= bounds.top + bounds.height;

      if (!isOnImage) {
        isPointerOnImage = false;
        renderDetails();
        return;
      }

      const stageRect = visualStage.getBoundingClientRect();
      const lensSize = lens.getBoundingClientRect().width || MAGNIFIER_SIZE;
      const radius = lensSize / 2;
      const centerX = clamp(pointerX - stageRect.left, radius, stageRect.width - radius);
      const centerY = clamp(pointerY - stageRect.top, radius, stageRect.height - radius);
      const sourceX = (pointerX - bounds.left) * MAGNIFIER_ZOOM;
      const sourceY = (pointerY - bounds.top) * MAGNIFIER_ZOOM;

      setMagnifierSource(magnifierStage);
      lens.style.backgroundSize = `${bounds.width * MAGNIFIER_ZOOM}px ${bounds.height * MAGNIFIER_ZOOM}px`;
      lens.style.backgroundPosition = `${radius - sourceX}px ${radius - sourceY}px`;
      detail.style.transform = `translate3d(${centerX - radius}px, ${centerY - radius}px, 0)`;
      isPointerOnImage = true;
      renderDetails();
    }

    function prepareMagnifier(stage) {
      magnifierStage = stage;
      magnifierReady = false;
      isPointerOnImage = false;
      window.clearTimeout(magnifierRevealTimer);
      setMagnifierSource(stage);
      renderDetails();

      if (!isMagnifierStage(stage) || mobileQuery.matches || !getDetailFocus(stage)) return;

      if (reducedMotionQuery.matches) {
        magnifierReady = true;
        updateMagnifier(lastPointer);
        return;
      }

      magnifierRevealTimer = window.setTimeout(() => {
        magnifierReady = true;
        updateMagnifier(lastPointer);
      }, MAGNIFIER_READY_DELAY);
    }

    function renderStableStage() {
      imageLayers.forEach((layer, index) => {
        const isCurrent = index === currentStage;
        layer.classList.remove('is-reveal-target');
        layer.style.opacity = isCurrent ? '1' : '0';
        layer.style.visibility = isCurrent ? 'visible' : 'hidden';
        layer.style.zIndex = isCurrent ? '1' : '0';
        layer.style.transform = 'none';
        layer.style.clipPath = 'none';
      });

      panel.classList.remove('is-transitioning');
      panel.style.setProperty('--final-ui-opacity', currentStage === 3 ? '0.82' : '1');
      if (grazingLight) grazingLight.style.opacity = currentStage === 1 || currentStage === 2 ? '0.18' : '0';
      magnifierStage = currentStage;
      magnifierReady = true;
      window.clearTimeout(magnifierRevealTimer);
      setMagnifierSource(currentStage);
      renderCurrentStage();
      renderDetails();
    }

    function commitTransitionStage(token, nextStage) {
      if (token !== transitionToken || currentStage === nextStage) return;
      currentStage = nextStage;
      renderCurrentStage();
    }

    function finishTransition(token, nextStage) {
      if (token !== transitionToken) return;
      commitTransitionStage(token, nextStage);
      renderStableStage();
    }

    function unlockTransition(token) {
      if (token !== transitionToken) return;
      isTransitioning = false;
      panel.classList.remove('is-transitioning');
      renderDetails();
    }

    function startTransition(fromStage, toStage) {
      const targetLayer = imageLayers[toStage];
      const fromLayer = imageLayers[fromStage];
      const focal = revealFocals[fromStage] || revealFocals[0];
      const token = transitionToken + 1;

      transitionToken = token;
      isTransitioning = true;
      window.clearTimeout(transitionTimer);
      window.clearTimeout(stageCommitTimer);
      window.clearTimeout(unlockTimer);
      panel.classList.add('is-transitioning');
      renderDetails();

      imageLayers.forEach((layer) => {
        layer.classList.remove('is-reveal-target');
        layer.style.opacity = '0';
        layer.style.visibility = 'hidden';
        layer.style.zIndex = '0';
        layer.style.transform = 'none';
        layer.style.clipPath = 'none';
      });

      fromLayer.style.opacity = '1';
      fromLayer.style.visibility = 'visible';
      fromLayer.style.zIndex = '1';
      fromLayer.style.clipPath = 'none';

      targetLayer.style.opacity = '1';
      targetLayer.style.visibility = 'visible';
      targetLayer.style.zIndex = '2';
      targetLayer.style.clipPath = `circle(0% at ${focal.x} ${focal.y})`;
      targetLayer.classList.add('is-reveal-target');

      window.requestAnimationFrame(() => {
        if (token !== transitionToken) return;
        targetLayer.style.clipPath = `circle(150% at ${focal.x} ${focal.y})`;
      });

      stageCommitTimer = window.setTimeout(
        () => commitTransitionStage(token, toStage),
        TRANSITION_MIDPOINT
      );
      transitionTimer = window.setTimeout(
        () => finishTransition(token, toStage),
        TRANSITION_DURATION
      );
      unlockTimer = window.setTimeout(() => unlockTransition(token), TRANSITION_LOCK);
    }

    function setStage(nextStage) {
      if (isTransitioning) return;

      const parsedStage = Number(nextStage);
      const normalizedStage = clamp(
        Number.isFinite(parsedStage) ? parsedStage : currentStage,
        0,
        stageCount - 1
      );
      if (normalizedStage === currentStage) {
        renderStableStage();
        return;
      }

      const fromStage = currentStage;
      prepareMagnifier(normalizedStage);

      if (reducedMotionQuery.matches) {
        currentStage = normalizedStage;
        renderStableStage();
        return;
      }

      startTransition(fromStage, normalizedStage);
    }

    function getWheelDelta(event) {
      if (event.deltaMode === 1) return event.deltaY * 16;
      if (event.deltaMode === 2) return event.deltaY * window.innerHeight;
      return event.deltaY;
    }

    function handleWheel(event) {
      if (mobileQuery.matches || isTransitioning) return;

      const delta = getWheelDelta(event);
      if (!delta) return;

      const direction = delta > 0 ? 1 : -1;
      const nextStage = currentStage + direction;

      if (nextStage < 0 || nextStage >= stageCount) {
        wheelAccumulator = 0;
        wheelDirection = 0;
        return;
      }

      if (direction !== wheelDirection) {
        wheelAccumulator = 0;
        wheelDirection = direction;
      }

      wheelAccumulator += Math.abs(delta);
      if (wheelAccumulator < WHEEL_THRESHOLD) return;

      wheelAccumulator = 0;
      wheelDirection = 0;
      event.preventDefault();
      setStage(nextStage);
    }

    function handleMediaChange() {
      wheelAccumulator = 0;
      wheelDirection = 0;
      window.clearTimeout(transitionTimer);
      window.clearTimeout(stageCommitTimer);
      window.clearTimeout(unlockTimer);
      window.clearTimeout(magnifierRevealTimer);
      transitionToken += 1;
      isTransitioning = false;
      magnifierStage = currentStage;
      magnifierReady = true;
      isPointerOnImage = false;
      renderStableStage();
    }

    const wheelHandler = (event) => handleWheel(event);
    const pointerEnterHandler = (event) => {
      if (event.pointerType === 'touch') return;
      isPointerInsideVisual = true;
      updateMagnifier(event);
    };
    const pointerMoveHandler = (event) => {
      if (event.pointerType === 'touch') return;
      updateMagnifier(event);
    };
    const pointerLeaveHandler = (event) => {
      if (event.pointerType === 'touch') return;
      isPointerInsideVisual = false;
      isPointerOnImage = false;
      lastPointer = null;
      renderDetails();
    };
    const linkHandlers = stageLinks.map((link) => {
      const handler = () => setStage(link.dataset.stageLink);
      link.addEventListener('click', handler);
      return { link, handler };
    });

    panel.addEventListener('wheel', wheelHandler, { passive: false });
    visualStage.addEventListener('pointerenter', pointerEnterHandler);
    visualStage.addEventListener('pointermove', pointerMoveHandler);
    visualStage.addEventListener('pointerleave', pointerLeaveHandler);
    mobileQuery.addEventListener('change', handleMediaChange);
    reducedMotionQuery.addEventListener('change', handleMediaChange);

    imageLayers.forEach((layer) => {
      const image = layer.querySelector('img');
      if (!image) {
        layer.setAttribute('data-image-state', 'missing');
        return;
      }

      const markLoaded = () => {
        layer.setAttribute('data-image-state', 'loaded');
        if (magnifierStage === index) {
          setMagnifierSource(index);
          updateMagnifier(lastPointer);
        }
      };
      const markMissing = () => layer.setAttribute('data-image-state', 'missing');

      if (image.complete && image.naturalWidth > 0) markLoaded();
      else if (image.complete) markMissing();
      else {
        image.addEventListener('load', markLoaded, { once: true });
        image.addEventListener('error', markMissing, { once: true });
      }
    });

    renderStableStage();

    const controller = {
      root,
      destroy() {
        window.clearTimeout(transitionTimer);
        window.clearTimeout(stageCommitTimer);
        window.clearTimeout(unlockTimer);
        window.clearTimeout(magnifierRevealTimer);
        transitionToken += 1;
        isTransitioning = false;
        panel.removeEventListener('wheel', wheelHandler);
        visualStage.removeEventListener('pointerenter', pointerEnterHandler);
        visualStage.removeEventListener('pointermove', pointerMoveHandler);
        visualStage.removeEventListener('pointerleave', pointerLeaveHandler);
        mobileQuery.removeEventListener('change', handleMediaChange);
        reducedMotionQuery.removeEventListener('change', handleMediaChange);
        linkHandlers.forEach(({ link, handler }) => link.removeEventListener('click', handler));
      },
    };

    registry.controllers.set(root, controller);
    return controller;
  }

  function removeControllersWithin(target) {
    registry.controllers.forEach((controller, root) => {
      if (target === root || (target.contains && target.contains(root))) {
        controller.destroy();
        registry.controllers.delete(root);
      }
    });
  }

  document.querySelectorAll(storySelector).forEach(createController);

  if (!registry.bound) {
    document.addEventListener('shopify:section:load', (event) => {
      const root = getRootFromEventTarget(event.target);
      if (root) createController(root);
    });
    document.addEventListener('shopify:section:unload', (event) => {
      removeControllersWithin(event.target);
    });
    registry.bound = true;
  }
})();
